import { useEffect, useRef, useState, type RefObject } from 'react';
import { env } from '@/env';
import { loadGoogleMapsPlaces, type GoogleAutocomplete } from '@/services/googleMapsLoader';
import { buildStreetAddressLine, getAddressComponentFromPlace } from '@/utils/autoComplete';

export type PlacesSelection = {
  street: string;
  city: string;
  state: string;
  zip: string;
};

export type UsePlacesAutocompleteArgs = {
  inputRef: RefObject<HTMLInputElement | null>;
  onSelect: (selection: PlacesSelection) => void;
  scrollRef?: RefObject<HTMLElement | null>;
  placeholder?: string;
};

export const usePlacesAutocomplete = ({
  inputRef,
  onSelect,
  scrollRef,
  placeholder,
}: UsePlacesAutocompleteArgs): { enabled: boolean } => {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const apiKey = env.GOOGLE_PLACES_API_KEY;
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    if (!apiKey) return undefined;

    let autocomplete: GoogleAutocomplete | undefined;
    let cancelled = false;
    let detachScroll: (() => void) | undefined;
    let restoreAuthFailure: (() => void) | undefined;

    loadGoogleMapsPlaces(apiKey)
      .then(() => {
        const input = inputRef.current;
        if (cancelled || !input || !window.google) return;

        // Captured before the widget attaches, because Google overwrites it
        // with "Oops! Something went wrong." when authentication fails.
        const ownPlaceholder = placeholder ?? input.placeholder;

        autocomplete = new window.google.maps.places.Autocomplete(input, {
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address'],
          types: ['address'],
        });

        if (placeholder !== undefined) input.placeholder = placeholder;

        // Maps JS authenticates lazily, on the member's first keystroke. An
        // invalid, referer-blocked, API-restricted or unbilled key therefore
        // fails mid-typing, and Google's own handling disables this input, wipes
        // the placeholder and tags it gm-err-autocomplete. /shipping has no
        // second address field, so that leaves the member unable to type and
        // Continue permanently disabled. Undo the damage and fall back to a
        // plain manual input; a missing suggestion list is a degradation, an
        // unusable form is a dead end.
        const previousAuthFailure = window.gm_authFailure;
        window.gm_authFailure = () => {
          previousAuthFailure?.();
          input.disabled = false;
          input.placeholder = ownPlaceholder;
          input.classList.remove('gm-err-autocomplete');
          // Maps JS also sets its error icon as an INLINE background-image, and
          // .rg-input declares no background-repeat, so it tiles across the
          // whole field. Clearing the class is not enough; the inline rule wins.
          input.style.backgroundImage = '';
          if (input.getAttribute('style') === '') input.removeAttribute('style');
          document.querySelectorAll('.pac-container').forEach((node) => node.remove());
          // Being disabled drops focus to <body>, so the keystrokes after the
          // failing one go nowhere. Only take focus back if it was lost rather
          // than moved, or this would yank the member out of a later field.
          const active = document.activeElement;
          if (!active || active === document.body) input.focus();
          if (autocomplete) window.google?.maps?.event?.clearInstanceListeners(autocomplete);
          setAuthFailed(true);
        };
        restoreAuthFailure = () => {
          window.gm_authFailure = previousAuthFailure;
        };

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete?.getPlace();
          if (!place?.address_components?.length) return;
          onSelectRef.current({
            street: buildStreetAddressLine(place),
            city: getAddressComponentFromPlace(place, 'locality') ?? '',
            state: getAddressComponentFromPlace(place, 'administrative_area_level_1') ?? '',
            zip: getAddressComponentFromPlace(place, 'postal_code') ?? '',
          });
        });

        const scroller = scrollRef?.current;
        if (scroller) {
          const hidePacOnScroll = () => {
            document.querySelectorAll('.pac-container').forEach((node) => {
              (node as HTMLElement).style.display = 'none';
            });
          };
          scroller.addEventListener('scroll', hidePacOnScroll, { passive: true });
          detachScroll = () => scroller.removeEventListener('scroll', hidePacOnScroll);
        }
      })
      .catch(() => {
        if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
          console.warn(
            '[usePlacesAutocomplete] Google Places script failed to load — the address field stays a plain manual input.',
          );
        }
      });

    return () => {
      cancelled = true;
      detachScroll?.();
      restoreAuthFailure?.();
      if (autocomplete) window.google?.maps?.event?.clearInstanceListeners(autocomplete);
      document.querySelectorAll('.pac-container').forEach((node) => node.remove());
    };
  }, [apiKey, inputRef, scrollRef, placeholder]);

  return { enabled: Boolean(apiKey) && !authFailed };
};
